// ---------------------------------------------------------------------------
// DSS-8: Full typography controls — font, size, weight, style, transform, etc.
// ---------------------------------------------------------------------------
import { useCallback, useId } from "react";
import { Type } from "lucide-react";
import FontPicker from "./font-picker";

// ── Types ──
export type CssUnit = "px" | "em" | "rem" | "vw";
export type LineHeightUnit = "px" | "em" | "";

export interface TypographyConfig {
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly fontSizeUnit: CssUnit;
  readonly fontWeight: number;
  readonly fontStyle: "normal" | "italic";
  readonly textTransform: "none" | "uppercase" | "lowercase" | "capitalize";
  readonly textDecoration: "none" | "underline" | "line-through" | "overline";
  readonly lineHeight: number;
  readonly lineHeightUnit: LineHeightUnit;
  readonly letterSpacing: number;
  readonly letterSpacingUnit: "px" | "em";
  readonly wordSpacing: number;
}

interface TypographyControlsProps {
  readonly value: TypographyConfig;
  readonly onChange: (config: TypographyConfig) => void;
}

// ── Constants ──
const SIZE_UNITS: CssUnit[] = ["px", "em", "rem", "vw"];
const LH_UNITS: LineHeightUnit[] = ["", "px", "em"];
const LS_UNITS: ("px" | "em")[] = ["px", "em"];
const MIN_FONT_SIZE = 8;
const MAX_FONT_SIZE = 200;

const WEIGHT_LABELS: Record<number, string> = {
  100: "Thin", 200: "Extra Light", 300: "Light", 400: "Regular",
  500: "Medium", 600: "Semi Bold", 700: "Bold", 800: "Extra Bold", 900: "Black",
};
const FONT_WEIGHTS = [100, 200, 300, 400, 500, 600, 700, 800, 900] as const;

const TRANSFORMS = ["none", "uppercase", "lowercase", "capitalize"] as const;
const DECORATIONS = ["none", "underline", "line-through", "overline"] as const;

// ── NaN-safe parser ──
function safeNum(raw: string, fallback: number): number {
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? fallback : parsed;
}

// ── Component ──
export default function TypographyControls({ value, onChange }: TypographyControlsProps) {
  const uid = useId();

  const update = useCallback(
    (patch: Partial<TypographyConfig>) => {
      onChange({ ...value, ...patch });
    },
    [value, onChange],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Type className="h-3.5 w-3.5" />
        Typography
      </div>

      <div className="flex flex-col gap-2.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
        <FontPicker value={value.fontFamily} onChange={(fontFamily) => update({ fontFamily })} />
        <NumericWithUnit id={`${uid}-size`} label="Size" value={value.fontSize}
          unit={value.fontSizeUnit} units={SIZE_UNITS} min={MIN_FONT_SIZE} max={MAX_FONT_SIZE}
          onChange={(fontSize) => update({ fontSize })}
          onUnitChange={(u) => update({ fontSizeUnit: u as CssUnit })} />
        <div className="flex items-center gap-2">
          <label
            htmlFor={`${uid}-weight`}
            className="w-20 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]"
          >
            Weight
          </label>
          <select
            id={`${uid}-weight`}
            value={value.fontWeight}
            onChange={(e) => update({ fontWeight: safeNum(e.target.value, 400) })}
            className="h-7 flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-2 text-[11px] text-[var(--color-foreground)]"
          >
            {FONT_WEIGHTS.map((w) => (
              <option key={w} value={w}>{w} - {WEIGHT_LABELS[w]}</option>
            ))}
          </select>
        </div>

        {/* Font style */}
        <ToggleRow
          label="Style"
          options={["normal", "italic"]}
          value={value.fontStyle}
          onChange={(fontStyle) => update({ fontStyle: fontStyle as "normal" | "italic" })}
        />

        {/* Text transform */}
        <ToggleRow
          label="Transform"
          options={[...TRANSFORMS]}
          value={value.textTransform}
          onChange={(v) => update({ textTransform: v as TypographyConfig["textTransform"] })}
        />

        {/* Text decoration */}
        <ToggleRow
          label="Decoration"
          options={[...DECORATIONS]}
          value={value.textDecoration}
          onChange={(v) => update({ textDecoration: v as TypographyConfig["textDecoration"] })}
        />

        <NumericWithUnit id={`${uid}-lh`} label="Line Height" value={value.lineHeight}
          unit={value.lineHeightUnit} units={LH_UNITS} min={0} max={10} step={0.1}
          onChange={(lineHeight) => update({ lineHeight })}
          onUnitChange={(u) => update({ lineHeightUnit: u as LineHeightUnit })} />
        <NumericWithUnit id={`${uid}-ls`} label="Letter Spc" value={value.letterSpacing}
          unit={value.letterSpacingUnit} units={LS_UNITS} min={-10} max={50} step={0.1}
          onChange={(letterSpacing) => update({ letterSpacing })}
          onUnitChange={(u) => update({ letterSpacingUnit: u as "px" | "em" })} />
        <NumericWithUnit id={`${uid}-ws`} label="Word Spc" value={value.wordSpacing}
          unit="px" units={["px"]} min={-10} max={50} step={0.5}
          onChange={(wordSpacing) => update({ wordSpacing })} onUnitChange={() => {}} />
      </div>

      {/* CSS output */}
      <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
        <code className="whitespace-pre-wrap text-[10px] text-[var(--color-muted-foreground)]">
          {generateTypographyCss(value)}
        </code>
      </div>
    </div>
  );
}

// ── Toggle row ──
interface ToggleRowProps {
  readonly label: string;
  readonly options: string[];
  readonly value: string;
  readonly onChange: (value: string) => void;
}

function ToggleRow({ label, options, value: active, onChange: onSelect }: ToggleRowProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]">
        {label}
      </span>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onSelect(opt)}
            className={[
              "rounded-md border px-2 py-1 text-[10px] font-medium capitalize",
              active === opt
                ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50",
            ].join(" ")}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Numeric input with unit selector ──
interface NumericWithUnitProps {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly unit: string;
  readonly units: readonly string[];
  readonly min: number;
  readonly max: number;
  readonly step?: number;
  readonly onChange: (value: number) => void;
  readonly onUnitChange: (unit: string) => void;
}

function NumericWithUnit({
  id,
  label,
  value: val,
  unit,
  units,
  min,
  max,
  step = 1,
  onChange: onNum,
  onUnitChange,
}: NumericWithUnitProps) {
  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor={id}
        className="w-20 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]"
      >
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={min}
        max={max}
        step={step}
        value={val}
        onChange={(e) => {
          const n = safeNum(e.target.value, val);
          onNum(Math.min(Math.max(n, min), max));
        }}
        className="h-7 w-16 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-2 text-[11px] tabular-nums text-[var(--color-foreground)]"
      />
      {units.length > 1 ? (
        <select
          value={unit}
          onChange={(e) => onUnitChange(e.target.value)}
          className="h-7 rounded-md border border-[var(--color-border)] bg-[var(--color-card)] px-1 text-[10px] text-[var(--color-muted-foreground)]"
        >
          {units.map((u) => (
            <option key={u} value={u}>
              {u || "unitless"}
            </option>
          ))}
        </select>
      ) : (
        <span className="text-[10px] text-[var(--color-muted-foreground)]">{unit}</span>
      )}
    </div>
  );
}

// ── CSS generation ──
export function generateTypographyCss(c: TypographyConfig): string {
  const l: string[] = [];
  if (c.fontFamily) l.push(`font-family: '${c.fontFamily}', sans-serif;`);
  l.push(`font-size: ${c.fontSize}${c.fontSizeUnit};`);
  l.push(`font-weight: ${c.fontWeight};`);
  if (c.fontStyle !== "normal") l.push(`font-style: ${c.fontStyle};`);
  if (c.textTransform !== "none") l.push(`text-transform: ${c.textTransform};`);
  if (c.textDecoration !== "none") l.push(`text-decoration: ${c.textDecoration};`);
  l.push(`line-height: ${c.lineHeight}${c.lineHeightUnit};`);
  if (c.letterSpacing !== 0) l.push(`letter-spacing: ${c.letterSpacing}${c.letterSpacingUnit};`);
  if (c.wordSpacing !== 0) l.push(`word-spacing: ${c.wordSpacing}px;`);
  return l.join("\n");
}
